import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Per-user UI preference hook backed by `user_ui_prefs_cache` (PostgreSQL).
 *
 * Replaces ad-hoc localStorage usage so prefs follow the user across browsers
 * and devices. Optimistically updates the local value on `setValue` and
 * fires a debounced PUT (350ms) so rapid toggles coalesce into a single
 * round-trip.
 *
 * Returns the same shape as `useState` plus an `isLoading` flag for the
 * initial fetch — callers can render with `defaultValue` immediately
 * (`isLoading=true`) and reconcile when the server response arrives.
 *
 * Failures fall back to the in-memory value silently. The hook never
 * throws; if PG is down the page degrades to non-persistent local state.
 */
export function useUserUiPref<T>(
  key: string,
  defaultValue: T,
  options?: { debounceMs?: number },
): [T, (next: T | ((prev: T) => T)) => void, { isLoading: boolean }] {
  const debounceMs = options?.debounceMs ?? 350;
  const queryClient = useQueryClient();
  const queryKey = ['user-ui-prefs', key] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await api.get<{ key: string; value: T | null }>(
          `/v4/user-ui-prefs/${encodeURIComponent(key)}`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          return { key, value: null };
        }
        throw e;
      }
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const serverValue = data?.value;
  const [localValue, setLocalValue] = useState<T>(defaultValue);
  const hasReconciled = useRef(false);

  useEffect(() => {
    if (hasReconciled.current) return;
    if (isLoading) return;
    if (serverValue !== undefined && serverValue !== null) {
      setLocalValue(serverValue);
    }
    hasReconciled.current = true;
  }, [isLoading, serverValue]);

  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const writeRef = useRef<T>(defaultValue);

  const flush = useCallback(async () => {
    const value = writeRef.current;
    try {
      await api.put(`/v4/user-ui-prefs/${encodeURIComponent(key)}`, { value });
      queryClient.setQueryData(queryKey, { key, value });
    } catch {
      // swallow — local state is already updated; prefs are non-critical
    }
  }, [key, queryClient, queryKey]);

  const setValue = useCallback((next: T | ((prev: T) => T)) => {
    setLocalValue((prev) => {
      const computed = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      writeRef.current = computed;
      if (pendingTimer.current) clearTimeout(pendingTimer.current);
      pendingTimer.current = setTimeout(flush, debounceMs);
      return computed;
    });
  }, [debounceMs, flush]);

  useEffect(() => () => {
    if (pendingTimer.current) {
      clearTimeout(pendingTimer.current);
      flush();
    }
  }, [flush]);

  return [localValue, setValue, { isLoading }];
}
